import type {
	ClientComplianceListHitDto,
	ClientComplianceStatusResponseDto,
	ComplianceAttentionTone,
} from '@supply-mover-2-0/shared';
import type { FilterOption } from '../../../filters/config';
import type { FilterRule } from '../../types';
import { LICENSE_COMPLIANCE_LIST_CONFIG } from './index';
import {
	COMPLIANCE_ATTENTION_LIST_IDS,
	type ComplianceAttentionListId,
} from './list-ids';

/**
 * PD-15873 — the client license badge reads the Compliance dashboard's own
 * lists, so the badge and the lists always agree.
 *
 * This file is the ONE place that names each list's title, tone and rank. The
 * dashboard cards read their titles and accent colors from here, and the badge
 * on the client overview reads the same entries, so a card and the badge can
 * never call the same problem by two names.
 */

export interface ComplianceAttentionListMeta {
	listId: ComplianceAttentionListId;
	/** The dashboard card's title; also the badge's label. */
	title: string;
	tone: ComplianceAttentionTone;
}

/**
 * The lists a client can be ON, in the dashboard's card order: the Fix Now
 * cards first, then the One Click cards. The badge's top reason is the most
 * severe list the client is on, the first in this order among equals.
 *
 * Recently Fixed is deliberately absent: it lists what Workd corrected, not a
 * problem the account still has.
 */
export const COMPLIANCE_ATTENTION_LIST_META: ReadonlyArray<ComplianceAttentionListMeta> =
	[
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.controlledNoDea,
			title: 'Controlled, No Verified DEA',
			tone: 'danger',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.deadLicense,
			title: 'Dead License',
			tone: 'danger',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.adverseAction,
			title: 'Adverse Action',
			tone: 'warning',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.wrongNumber,
			title: 'Wrong Number on File',
			tone: 'danger',
		},
		{
			// PD-16384: the "failed lookup" item. Plain words on purpose — the
			// customer reads it as "the board has no license with this number".
			listId: COMPLIANCE_ATTENTION_LIST_IDS.failedLookup,
			title: 'Number Not Found',
			tone: 'danger',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.expiringSoon,
			title: 'Expiring Soon',
			tone: 'warning',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.noVerifiedLicense,
			title: 'No Verified License',
			tone: 'warning',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.linkEnded,
			title: 'Link Ended',
			tone: 'warning',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.readyToConfirm,
			title: 'Ready to Confirm',
			tone: 'primary',
		},
		{
			listId: COMPLIANCE_ATTENTION_LIST_IDS.replacementAvailable,
			title: 'Replacement Available',
			tone: 'primary',
		},
	];

/** One list's meta by id. Throws for an id that is not an attention list. */
export function complianceAttentionListMeta(
	listId: ComplianceAttentionListId,
): ComplianceAttentionListMeta {
	const meta = COMPLIANCE_ATTENTION_LIST_META.find(
		(entry) => entry.listId === listId,
	);
	if (meta == null) {
		throw new Error(`No compliance attention list meta for ${listId}`);
	}
	return meta;
}

function filterOptionId(option: FilterOption | string): string | null {
	if (typeof option === 'string') {
		return option;
	}
	const id: unknown = (option as { id?: unknown }).id;
	return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
}

/**
 * The filters a compliance list applies for EVERY user as it opens: each
 * rule's `defaultValue` that carries no permission condition, as the ids the
 * `/licenses` endpoint receives (`{ licenseActiveBuyer: ['yes'], … }`).
 *
 * Read straight off the list definition, so the badge asks the license query
 * exactly what the list asks it. The own-accounts scope is conditional
 * (`applyDefaultIf`) and so is not here: the badge is already scoped to one
 * client, which the caller adds as `licenseClient`.
 */
export function complianceListDefaultFilters(
	listId: ComplianceAttentionListId,
): Record<string, Array<string>> {
	const list = LICENSE_COMPLIANCE_LIST_CONFIG.lists.find(
		(entry) => entry.id === listId,
	);
	if (list == null) {
		throw new Error(`No compliance list ${listId}`);
	}
	const filters: Record<string, Array<string>> = {};
	const rules: Array<[string, FilterRule]> = Object.entries(
		list.filterRules ?? {},
	);
	for (const [key, rule] of rules) {
		if (rule.defaultValue == null || rule.applyDefaultIf != null) {
			continue;
		}
		const raw = rule.defaultValue;
		// A bare object default (a date range) is not an id list and no
		// compliance list uses one; it is skipped rather than guessed at.
		const options: Array<FilterOption | string> = Array.isArray(raw)
			? raw
			: typeof raw === 'string'
				? [raw]
				: [];
		const ids = options
			.map((option) => filterOptionId(option))
			.filter((id): id is string => id != null && id !== '');
		if (ids.length > 0) {
			filters[key] = ids;
		}
	}
	return filters;
}

const TONE_SEVERITY: Record<ComplianceAttentionTone, number> = {
	danger: 0,
	warning: 1,
	primary: 2,
};

/** The badge's state, label and tone from the lists the client is on. */
export function summarizeClientComplianceStatus(input: {
	lists: Array<ClientComplianceListHitDto>;
	verified: boolean;
	activeBuyer: boolean;
}): ClientComplianceStatusResponseDto {
	const rank = (listId: string): number =>
		COMPLIANCE_ATTENTION_LIST_META.findIndex((m) => m.listId === listId);
	const lists = [...input.lists].sort(
		(a, b) => rank(a.listId) - rank(b.listId),
	);
	// The top reason is the most severe list, then the first in dashboard order:
	// Wrong Number (danger) outranks Adverse Action (warning) above it.
	const top = [...lists]
		.sort((a, b) => {
			const bySeverity = TONE_SEVERITY[a.tone] - TONE_SEVERITY[b.tone];
			return bySeverity !== 0 ? bySeverity : rank(a.listId) - rank(b.listId);
		})
		.at(0);
	if (top != null) {
		return {
			enabled: true,
			state: 'attention',
			tone: top.tone,
			label: top.title,
			activeBuyer: input.activeBuyer,
			lists,
		};
	}
	return {
		enabled: true,
		state: input.verified ? 'licensed' : 'not_verified',
		tone: input.verified ? 'success' : 'default',
		label: input.verified ? 'Licensed' : 'Not Verified',
		activeBuyer: input.activeBuyer,
		lists: [],
	};
}

/** The response for a tenant that does not use licensing. */
export const CLIENT_COMPLIANCE_STATUS_DISABLED: ClientComplianceStatusResponseDto =
	{
		enabled: false,
		state: null,
		tone: null,
		label: null,
		activeBuyer: false,
		lists: [],
	};
